"use client"

import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { zodResolver } from "@hookform/resolvers/zod"
import { Check, Clipboard, Loader2, RefreshCw } from "lucide-react"
import { motion } from "motion/react"
import { useForm, useWatch } from "react-hook-form"

import {
  formSchema,
  FormValues,
  MAX_DESCRIPTION_LENGTH,
  MAX_SCOPE_LENGTH,
  MAX_SNIPPET_LENGTH,
  MAX_TAB_TRIGGER_LENGTH,
} from "@/lib/schema"
import { DEFAULT_STATE, FormatKey } from "@/lib/types"
import { formatDefinitions } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { ScrollReveal } from "@/components/scroll-reveal"

import { Footer } from "../home/footer"
import { Header } from "../home/header"
import { PasteSnippetDialog } from "./paste-snippet-dialog"

function GenerateSuspense() {
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()

  const stateFromQuery = useMemo<FormValues>(() => {
    const params = searchParams
    if (!params) return DEFAULT_STATE

    const formatParam = params.get("format")
    const descriptionParam = params.get("description")
    const tabTriggerParam = params.get("tabTrigger")
    const snippetParam = params.get("snippet")
    const scopeParam = params.get("scope")

    const next: FormValues = { ...DEFAULT_STATE }
    if (
      formatParam === FormatKey.VSCODE ||
      formatParam === FormatKey.SUBLIME ||
      formatParam === FormatKey.ATOM ||
      formatParam === FormatKey.ZED ||
      formatParam === FormatKey.JUPYTER
    ) {
      next.format = formatParam
    }
    if (descriptionParam !== null)
      next.description = descriptionParam.slice(0, MAX_DESCRIPTION_LENGTH)
    if (tabTriggerParam !== null)
      next.tabTrigger = tabTriggerParam.slice(0, MAX_TAB_TRIGGER_LENGTH)
    if (snippetParam !== null)
      next.snippet = snippetParam.slice(0, MAX_SNIPPET_LENGTH)
    if (scopeParam !== null) next.scope = scopeParam.slice(0, MAX_SCOPE_LENGTH)
    return next
  }, [searchParams])

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: stateFromQuery,
  })

  const description = useWatch({ control: form.control, name: "description" })
  const tabTrigger = useWatch({ control: form.control, name: "tabTrigger" })
  const snippet = useWatch({ control: form.control, name: "snippet" })
  const format = useWatch({ control: form.control, name: "format" })
  const scope = useWatch({ control: form.control, name: "scope" })
  const currentState = useMemo<FormValues>(
    () => ({
      description: description || "",
      tabTrigger: tabTrigger || "",
      snippet: snippet || "",
      format: format || FormatKey.VSCODE,
      scope: scope,
    }),
    [description, tabTrigger, snippet, format, scope]
  )

  const output = useMemo(
    () => formatDefinitions[currentState.format].build(currentState),
    [currentState]
  )

  const codeRef = useRef<HTMLElement | null>(null)
  const snippetTextareaRef = useRef<HTMLTextAreaElement | null>(null)
  const [outputSelected, setOutputSelected] = useState(false)
  const [copied, setCopied] = useState(false)
  const lastSyncedQuery = useRef<string | null>(null)
  const copyTimeoutRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current !== null) {
        clearTimeout(copyTimeoutRef.current)
      }
    }
  }, [])

  const serializeState = useCallback((state: FormValues) => {
    const params = new URLSearchParams()
    if (state.description)
      params.set(
        "description",
        state.description.slice(0, MAX_DESCRIPTION_LENGTH)
      )
    if (state.tabTrigger)
      params.set(
        "tabTrigger",
        state.tabTrigger.slice(0, MAX_TAB_TRIGGER_LENGTH)
      )
    if (state.snippet)
      params.set("snippet", state.snippet.slice(0, MAX_SNIPPET_LENGTH))
    params.set("format", state.format)
    if (state.scope) params.set("scope", state.scope.slice(0, MAX_SCOPE_LENGTH))
    return params.toString()
  }, [])

  useEffect(() => {
    const queryString = serializeState(currentState)
    if (lastSyncedQuery.current === queryString) return
    lastSyncedQuery.current = queryString
    router.replace(queryString ? `${pathname}?${queryString}` : pathname, {
      scroll: false,
    })
  }, [currentState, pathname, router, serializeState])

  useEffect(() => {
    const queryString = serializeState(stateFromQuery)
    if (lastSyncedQuery.current === queryString) return
    form.reset(stateFromQuery)
    lastSyncedQuery.current = queryString
  }, [stateFromQuery, form, serializeState])

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(output)
      setCopied(true)
      if (copyTimeoutRef.current !== null) {
        clearTimeout(copyTimeoutRef.current)
      }
      copyTimeoutRef.current = window.setTimeout(() => {
        setCopied(false)
        copyTimeoutRef.current = null
      }, 1600)
    } catch {
      try {
        const ta = document.createElement("textarea")
        ta.value = output
        document.body.appendChild(ta)
        ta.select()
        document.execCommand("copy")
        document.body.removeChild(ta)
        setCopied(true)
        if (copyTimeoutRef.current !== null) {
          clearTimeout(copyTimeoutRef.current)
        }
        copyTimeoutRef.current = window.setTimeout(() => {
          setCopied(false)
          copyTimeoutRef.current = null
        }, 1600)
      } catch {}
    }
  }, [output])

  const handleToggleSelectOutput = useCallback(() => {
    if (typeof window === "undefined" || !codeRef.current) return
    const selection = window.getSelection()
    if (!selection) return

    if (outputSelected) {
      selection.removeAllRanges()
      setOutputSelected(false)
      return
    }
    const range = document.createRange()
    range.selectNodeContents(codeRef.current)
    selection.removeAllRanges()
    selection.addRange(range)
    setOutputSelected(true)
  }, [outputSelected])

  useEffect(() => {
    const onSelectionChange = () => {
      const sel = window.getSelection()
      if (!sel || sel.rangeCount === 0) {
        setOutputSelected(false)
        return
      }
      if (!codeRef.current) {
        setOutputSelected(false)
        return
      }
      const range = sel.getRangeAt(0)
      if (range.intersectsNode && range.intersectsNode(codeRef.current)) {
        setOutputSelected(true)
      } else {
        setOutputSelected(false)
      }
    }
    document.addEventListener("selectionchange", onSelectionChange)
    return () =>
      document.removeEventListener("selectionchange", onSelectionChange)
  }, [])

  const handleReset = useCallback(() => {
    form.reset(DEFAULT_STATE)
  }, [form])

  const updateField = useCallback(
    (field: keyof FormValues, value: string) => {
      form.setValue(field, value, { shouldDirty: true, shouldTouch: true })
    },
    [form]
  )

  const handleSubmit = useCallback(
    (data: FormValues) => {
      form.reset(data)
    },
    [form]
  )

  const handlePasteSnippet = useCallback(
    (data: FormValues) => {
      form.reset(data)
    },
    [form]
  )

  const getNextPlaceholderNumber = useCallback((text: string): number => {
    const placeholderRegex = /\$\{(\d+)(?::[^}]*)?\}/g
    const matches = Array.from(text.matchAll(placeholderRegex))
    const usedNumbers = new Set(matches.map((match) => parseInt(match[1], 10)))
    let nextNumber = 1
    while (usedNumbers.has(nextNumber)) {
      nextNumber++
    }
    return nextNumber
  }, [])

  const handleInsertPlaceholder = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const textarea = event.currentTarget
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const selectedText = textarea.value.substring(start, end)
      const currentValue = textarea.value

      const placeholderNumber = getNextPlaceholderNumber(currentValue)

      const defaultValue = selectedText.trim() || "example"
      const placeholder = `\${${placeholderNumber}:${defaultValue}}`

      const newValue =
        currentValue.substring(0, start) +
        placeholder +
        currentValue.substring(end)

      form.setValue("snippet", newValue, {
        shouldDirty: true,
        shouldTouch: true,
      })

      setTimeout(() => {
        if (snippetTextareaRef.current) {
          const newCursorPos = start + placeholder.length
          snippetTextareaRef.current.setSelectionRange(
            newCursorPos,
            newCursorPos
          )
          snippetTextareaRef.current.focus()
        }
      }, 0)
    },
    [form, getNextPlaceholderNumber]
  )

  const handleSnippetKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "i") {
        event.preventDefault()
        handleInsertPlaceholder(event)
      }
    },
    [handleInsertPlaceholder]
  )

  return (
    <div className="min-h-screen pt-16 transition-colors">
      <Header />
      <main className="bg-muted/20 min-h-screen w-full border-b py-6 sm:py-10">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <ScrollReveal y={10}>
                <p className="text-primary mb-2 text-xs font-semibold tracking-wider uppercase">
                  Generator
                </p>
              </ScrollReveal>
              <ScrollReveal delay={0.05} y={14}>
                <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
                  Create your snippet
                </h1>
              </ScrollReveal>
              <ScrollReveal delay={0.1} y={14}>
                <p className="text-muted-foreground mt-2 max-w-2xl text-sm">
                  Write once. Preview editor-ready output instantly.
                </p>
              </ScrollReveal>
            </div>
            <ScrollReveal delay={0.15} y={12}>
              <p className="text-muted-foreground hidden text-xs sm:block">
                Tip:{" "}
                <kbd className="text-foreground bg-background rounded border px-1.5 py-1 font-mono">
                  ⌘ I
                </kbd>{" "}
                inserts placeholder
              </p>
            </ScrollReveal>
          </div>
          <div className="bg-card grid overflow-hidden rounded-2xl border xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
            <ScrollReveal
              y={18}
              className="border-b p-4 sm:p-6 xl:border-r xl:border-b-0"
            >
              <div className="mb-6 flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold">Source</h2>
                  <p className="text-muted-foreground mt-1 text-xs">
                    Snippet metadata + code
                  </p>
                </div>
                {currentState.snippet &&
                  currentState.tabTrigger &&
                  currentState.description && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleReset}
                      aria-label="Reset to defaults"
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Reset
                    </Button>
                  )}
              </div>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(handleSubmit)}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="tabTrigger"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center justify-between gap-3">
                            <FormLabel>Tab trigger</FormLabel>
                            <span className="text-muted-foreground text-xs">
                              {field.value?.length ?? 0}/
                              {MAX_TAB_TRIGGER_LENGTH}
                            </span>
                          </div>
                          <FormControl>
                            <Input
                              {...field}
                              onChange={(event) => {
                                const value = event.target.value.slice(
                                  0,
                                  MAX_TAB_TRIGGER_LENGTH
                                )
                                field.onChange(value)
                              }}
                              maxLength={MAX_TAB_TRIGGER_LENGTH}
                              placeholder="e.g. log"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="scope"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center justify-between gap-3">
                            <FormLabel>Language scope</FormLabel>
                            <span className="text-muted-foreground text-xs">
                              {field.value?.length ?? 0}/{MAX_SCOPE_LENGTH}
                            </span>
                          </div>
                          <FormControl>
                            <Input
                              {...field}
                              value={field.value ?? ""}
                              onChange={(event) => {
                                const value = event.target.value.slice(
                                  0,
                                  MAX_SCOPE_LENGTH
                                )
                                field.onChange(value)
                              }}
                              onBlur={(event) => {
                                const value = event.target.value.trim()
                                if (value === "") {
                                  field.onChange(undefined)
                                } else if (value !== field.value) {
                                  field.onChange(value)
                                }
                              }}
                              maxLength={MAX_SCOPE_LENGTH}
                              placeholder="e.g. javascript, typescript"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center justify-between gap-3">
                            <FormLabel>Description</FormLabel>
                            <span className="text-muted-foreground text-xs">
                              {field.value?.length ?? 0}/
                              {MAX_DESCRIPTION_LENGTH}
                            </span>
                          </div>
                          <FormControl>
                            <Textarea
                              {...field}
                              className="h-24 resize-none"
                              onChange={(event) => {
                                const value = event.target.value.slice(
                                  0,
                                  MAX_DESCRIPTION_LENGTH
                                )
                                field.onChange(value)
                              }}
                              maxLength={MAX_DESCRIPTION_LENGTH}
                              placeholder="What does this snippet do?"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={form.control}
                    name="snippet"
                    render={({ field }) => (
                      <FormItem className="relative">
                        <div className="flex items-center justify-between gap-3">
                          <FormLabel>Snippet code</FormLabel>
                          <span className="text-muted-foreground text-xs">
                            {field.value?.length ?? 0}/{MAX_SNIPPET_LENGTH}
                          </span>
                        </div>
                        <FormControl>
                          <Textarea
                            {...field}
                            ref={(e) => {
                              field.ref(e)
                              snippetTextareaRef.current = e
                            }}
                            onChange={(event) => {
                              const value = event.target.value.slice(
                                0,
                                MAX_SNIPPET_LENGTH
                              )
                              field.onChange(value)
                            }}
                            onKeyDown={handleSnippetKeyDown}
                            maxLength={MAX_SNIPPET_LENGTH}
                            rows={14}
                            spellCheck={false}
                            className="h-80 resize-none font-mono text-sm"
                            placeholder="Paste or write snippet code…"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </form>
              </Form>
            </ScrollReveal>

            <ScrollReveal delay={0.08} y={18} className="min-w-0 p-4 sm:p-6">
              <div className="mb-5 flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
                <div>
                  <h2 className="text-sm font-semibold">Preview</h2>
                  <p className="text-muted-foreground mt-1 text-xs">
                    Live, schema-valid output
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <PasteSnippetDialog onParse={handlePasteSnippet} />
                  <Button size="sm" onClick={handleCopy} aria-live="polite">
                    {copied ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Clipboard className="h-4 w-4" />
                    )}
                    {copied ? "Copied" : "Copy output"}
                  </Button>
                </div>
              </div>
              <div className="bg-muted mb-4 grid grid-cols-2 gap-1 rounded-xl p-1 sm:grid-cols-3 lg:grid-cols-5">
                {Object.entries(formatDefinitions).map(([key, value]) => {
                  const active = currentState.format === key
                  return (
                    <motion.div
                      key={key}
                      layout
                      animate={{
                        scale: active ? 1 : 0.98,
                        opacity: active ? 1 : 0.72,
                      }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                    >
                      <Button
                        type="button"
                        variant={active ? "default" : "ghost"}
                        onClick={() => updateField("format", key)}
                        aria-pressed={active}
                        className="h-9 w-full text-xs"
                      >
                        {value.label}
                      </Button>
                    </motion.div>
                  )
                })}
              </div>

              <motion.div
                key={currentState.format}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
                className="border-input bg-background relative overflow-hidden rounded-xl border"
              >
                <pre
                  className="min-h-96 overflow-auto p-4 font-mono text-sm leading-relaxed sm:p-5 xl:h-[calc(100vh-325px)]"
                  onClick={handleToggleSelectOutput}
                  onMouseDown={(event) => {
                    event.preventDefault()
                  }}
                  role="button"
                  tabIndex={0}
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <code ref={codeRef}>{output}</code>
                </pre>
              </motion.div>
            </ScrollReveal>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export function Generate() {
  return (
    <Suspense
      fallback={
        <div className="flex h-screen items-center justify-center gap-2 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      }
    >
      <GenerateSuspense />
    </Suspense>
  )
}
